import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon, ArrowLeftIcon, SendIcon } from 'lucide-react';
import { Button } from '../ui/Button';
export function ProjectRequestWizard() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    existingWebsite: '',
    goals: []
  });
  const [submitted, setSubmitted] = useState(false);
  const totalSteps = 4;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value,
      checked
    } = e.target;
    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          goals: [...prev.goals, value]
        };
      } else {
        return {
          ...prev,
          goals: prev.goals.filter(goal => goal !== value)
        };
      }
    });
  };
  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would handle the form submission here
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };
  return <div className="bg-white dark:bg-[#1E1E1E] rounded-xl shadow-md overflow-hidden" data-id="element-43">
      {!submitted ? <div className="p-6 md:p-8" data-id="element-44">
          {/* Progress Bar */}
          <div className="mb-8" data-id="element-45">
            <div className="flex justify-between mb-2" data-id="element-46">
              {Array.from({
            length: totalSteps
          }).map((_, index) => <div key={index} className={`w-8 h-8 rounded-full flex items-center justify-center ${step > index + 1 ? 'bg-[#3AA6F9] text-white' : step === index + 1 ? 'border-2 border-[#3AA6F9] text-[#3AA6F9]' : 'border-2 border-gray-200 dark:border-gray-700 text-gray-400'}`} data-id="element-47">
                  {step > index + 1 ? <CheckIcon size={16} data-id="element-48" /> : index + 1}
                </div>)}
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden" data-id="element-49">
              <div className="bg-[#3AA6F9] h-full transition-all duration-300" style={{
            width: `${step / totalSteps * 100}%`
          }} data-id="element-50"></div>
            </div>
          </div>
          <form onSubmit={handleSubmit} data-id="element-51">
            <AnimatePresence mode="wait" data-id="element-52">
              {step === 1 && <motion.div key="step1" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-53">
                  <h3 className="text-xl font-semibold mb-6" data-id="element-54">
                    Basic Information
                  </h3>
                  <div className="space-y-4" data-id="element-55">
                    <div data-id="element-56">
                      <label className="block text-sm font-medium mb-1" data-id="element-57">
                        Your Name *
                      </label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-58" />
                    </div>
                    <div data-id="element-59">
                      <label className="block text-sm font-medium mb-1" data-id="element-60">
                        Email Address *
                      </label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-61" />
                    </div>
                    <div data-id="element-62">
                      <label className="block text-sm font-medium mb-1" data-id="element-63">
                        Phone Number (Optional)
                      </label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" data-id="element-64" />
                    </div>
                    <div data-id="element-65">
                      <label className="block text-sm font-medium mb-1" data-id="element-66">
                        Company/Business Name *
                      </label>
                      <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-67" />
                    </div>
                  </div>
                </motion.div>}
              {step === 2 && <motion.div key="step2" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-68">
                  <h3 className="text-xl font-semibold mb-6" data-id="element-69">
                    Project Details
                  </h3>
                  <div className="space-y-4" data-id="element-70">
                    <div data-id="element-71">
                      <label className="block text-sm font-medium mb-1" data-id="element-72">
                        Project Type *
                      </label>
                      <select name="projectType" value={formData.projectType} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-73">
                        <option value="" data-id="element-74">Select project type</option>
                        <option value="new-website" data-id="element-75">New Website</option>
                        <option value="website-redesign" data-id="element-76">
                          Website Redesign
                        </option>
                        <option value="ecommerce" data-id="element-77">E-commerce Website</option>
                        <option value="maintenance" data-id="element-78">Website Maintenance</option>
                        <option value="other" data-id="element-79">Other</option>
                      </select>
                    </div>
                    <div data-id="element-80">
                      <label className="block text-sm font-medium mb-1" data-id="element-81">
                        Budget Range *
                      </label>
                      <select name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-82">
                        <option value="" data-id="element-83">Select budget range</option>
                        <option value="<500" data-id="element-84">Less than £500</option>
                        <option value="500-1000" data-id="element-85">£500 - £1,000</option>
                        <option value="1000-2000" data-id="element-86">£1,000 - £2,000</option>
                        <option value=">2000" data-id="element-87">More than £2,000</option>
                      </select>
                    </div>
                    <div data-id="element-88">
                      <label className="block text-sm font-medium mb-1" data-id="element-89">
                        Timeline *
                      </label>
                      <select name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-90">
                        <option value="" data-id="element-91">Select timeline</option>
                        <option value="asap" data-id="element-92">As soon as possible</option>
                        <option value="1-month" data-id="element-93">Within 1 month</option>
                        <option value="1-3-months" data-id="element-94">1-3 months</option>
                        <option value="3+-months" data-id="element-95">3+ months</option>
                      </select>
                    </div>
                    <div data-id="element-96">
                      <label className="block text-sm font-medium mb-1" data-id="element-97">
                        Do you have an existing website?
                      </label>
                      <input type="text" name="existingWebsite" value={formData.existingWebsite} onChange={handleChange} placeholder="If yes, please provide the URL" className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" data-id="element-98" />
                    </div>
                  </div>
                </motion.div>}
              {step === 3 && <motion.div key="step3" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-99">
                  <h3 className="text-xl font-semibold mb-6" data-id="element-100">Project Goals</h3>
                  <div className="space-y-4" data-id="element-101">
                    <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-102">
                      What are your primary goals for this project? (Select all
                      that apply)
                    </p>
                    <div className="space-y-3" data-id="element-103">
                      {[{
                  value: 'increase-traffic',
                  label: 'Increase website traffic'
                }, {
                  value: 'improve-conversions',
                  label: 'Improve conversion rates'
                }, {
                  value: 'better-user-experience',
                  label: 'Better user experience'
                }, {
                  value: 'establish-online-presence',
                  label: 'Establish online presence'
                }, {
                  value: 'sell-products',
                  label: 'Sell products online'
                }, {
                  value: 'generate-leads',
                  label: 'Generate leads'
                }, {
                  value: 'improve-brand-image',
                  label: 'Improve brand image'
                }].map(goal => <label key={goal.value} className="flex items-start" data-id="element-104">
                          <input type="checkbox" name="goals" value={goal.value} onChange={handleCheckboxChange} checked={formData.goals.includes(goal.value)} className="mt-1 mr-3" data-id="element-105" />
                          <span className="text-gray-700 dark:text-gray-300" data-id="element-106">
                            {goal.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </motion.div>}
              {step === 4 && <motion.div key="step4" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-107">
                  <h3 className="text-xl font-semibold mb-6" data-id="element-108">
                    Additional Information
                  </h3>
                  <div className="space-y-4" data-id="element-109">
                    <div data-id="element-110">
                      <label className="block text-sm font-medium mb-1" data-id="element-111">
                        Project Description *
                      </label>
                      <textarea name="description" value={formData.description} onChange={handleChange} rows={5} placeholder="Please provide any additional details about your project that will help us understand your needs better." className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-[#3AA6F9]" required data-id="element-112"></textarea>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-113">
                      By submitting this form, you agree to our{' '}
                      <a href="/privacy-policy" className="text-[#3AA6F9] hover:underline" data-id="element-114">
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
            <div className="mt-8 flex justify-between" data-id="element-115">
              {step > 1 ? <Button type="button" variant="outline" onClick={prevStep} data-id="element-116">
                  <ArrowLeftIcon size={16} className="mr-2" data-id="element-117" />
                  Previous
                </Button> : <div data-id="element-118"></div>}
              {step < totalSteps ? <Button type="button" onClick={nextStep} data-id="element-119">
                  Next
                  <ArrowRightIcon size={16} className="ml-2" data-id="element-120" />
                </Button> : <Button type="submit" data-id="element-121">
                  Send request
                  <SendIcon size={16} className="ml-2" data-id="element-122" />
                </Button>}
            </div>
          </form>
        </div> : <div className="p-8 text-center" data-id="element-123">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-124">
            <CheckIcon size={32} className="text-green-600" data-id="element-125" />
          </div>
          <h3 className="text-2xl font-bold mb-4" data-id="element-126">Thank You!</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-127">
            We've received your project request and will get back to you within
            24 hours to discuss your needs in more detail.
          </p>
          <Button onClick={() => {
        setSubmitted(false);
        setStep(1);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          budget: '',
          timeline: '',
          description: '',
          existingWebsite: '',
          goals: []
        });
      }} data-id="element-128">
            Send another request
          </Button>
        </div>}
    </div>;
}